/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { Image, IImageSettings } from '@msdyn365-commerce/core';
import {
    Heading
} from '@msdyn365-commerce-modules/utilities';

import { IDefaActionTileData } from './defa-action-tile.data';
import { IDefaActionTileProps } from './defa-action-tile.props.autogenerated';
import { isOnHomePage } from '../../shared-utils/common-utilities';

export interface IDefaActionTileViewProps extends IDefaActionTileProps<IDefaActionTileData> {
    baseClassName?: string;
    backgroundImage?: JSX.Element | null;
    heading?: JSX.Element | null;
    actionLink?: JSX.Element | null;
}

/**
 *
 * DefaActionTile component
 * @extends {React.PureComponent<IDefaActionTileProps<IDefaActionTileData>>}
 */
class DefaActionTile extends React.PureComponent<IDefaActionTileProps<IDefaActionTileData>> {

    private readonly baseClassName: string = 'defa-action-tile';

    public render(): JSX.Element | null {
        const viewProps: IDefaActionTileViewProps = {
            ...this.props,
            baseClassName: this.baseClassName,
            backgroundImage: this._renderBackgroundImage(),
            heading: this._renderHeading(),
            actionLink: this._renderActionLink()
        };
        return this.props.renderView(viewProps);
    }

    private _renderBackgroundImage(): JSX.Element | null {
        const { config, context } = this.props;

        if (!config.image) {
            return null;
        }

        const { image } = config;
        const Width = 800;
        const Height = 300;
        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { w: Width, h: Height, q: `w=${Width}px&h=${Height}&q=80&m=0&f=webp`, qe: `t=${Width}h${Height}qhigh` },
                lg: { w: Width, h: Height, q: `w=${Width}px&h=${Height}&q=80&m=0&f=webp`, qe: `t=${Width}h${Height}qhigh` },
                xl: { w: Width, h: Height, q: `w=${Width}px&h=${Height}&q=80&m=0&f=webp`, qe: `t=${Width}h${Height}qhigh` }
            },
            disableLazyLoad: false,
            lazyload: false,
            quality: 80
        };

        return (
            <Image
                requestContext={context.actionContext.requestContext}
                className={`${this.baseClassName}__background-image`}
                {...image}
                gridSettings={context.request.gridSettings!}
                imageSettings={defaultImageSettings}
                loadFailureBehavior='default'
                bypassHideOnFailure={true}
            />
        );
    }

    private _renderHeading(): JSX.Element | null {
        const { config } = this.props;

        if (!config.heading) {
            return null;
        }

        return (
            <Heading className={`${this.baseClassName}__heading`} {...config.heading} />
        );
    }

    private _renderActionLink(): JSX.Element | null {
        const { config, context } = this.props;

        if (!config.actionLink) {
            return null;
        }

        let href = config.actionLink?.destinationUrl;

        if (href && isOnHomePage(context) && config.landingPageSignInRedirectUrl?.destinationUrl) {
            try {
                const url = new URL(href);

                if (url.pathname.endsWith('/_msdyn365/signin')) {
                    url.searchParams.set('ru', `${context.request.url.requestUrl.origin}${config.landingPageSignInRedirectUrl.destinationUrl}`);
                    href = url.toString();
                }
            } catch {
                // Do nothing if URL parsing fails
            }
        }

        return (
            <a className={`${this.baseClassName}__action-link`} href={href}>
                {config.actionLabel}
            </a>
        );
    }
}

export default DefaActionTile;
